import { useCallback, useState } from 'react';
import { Platform } from 'react-native';

interface FocusTargetEvent {
  target?: unknown;
  nativeEvent?: { target?: unknown };
}

function matchesFocusVisible(event: FocusTargetEvent): boolean {
  const target = (event.nativeEvent?.target ?? event.target) as
    | { matches?: (selector: string) => boolean }
    | undefined;
  try {
    return target?.matches?.(':focus-visible') ?? false;
  } catch {
    return false;
  }
}

/**
 * Keyboard-only focus state for Pressables. react-native-web reports
 * `focused` after mouse clicks too, so a focus ring driven by it lingers
 * after clicking; this follows the browser's `:focus-visible` heuristic.
 */
export function useFocusVisible() {
  const [isFocusVisible, setIsFocusVisible] = useState(false);

  const onFocus = useCallback((event: FocusTargetEvent) => {
    setIsFocusVisible(Platform.OS === 'web' && matchesFocusVisible(event));
  }, []);

  const onBlur = useCallback(() => {
    setIsFocusVisible(false);
  }, []);

  return { isFocusVisible, onFocus, onBlur };
}
